import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { TESTIMONIALS } from "../data/site";

export const Testimonials = () => {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);

    const go = useCallback((d) => {
        setDir(d);
        setIndex((i) => (i + d + TESTIMONIALS.length) % TESTIMONIALS.length);
    }, []);

    useEffect(() => {
        const t = setInterval(() => go(1), 6000);
        return () => clearInterval(t);
    }, [go]);

    const t = TESTIMONIALS[index];

    return (
        <section data-testid="testimonials-section" className="py-28 lg:py-36 px-6 overflow-hidden">
            <div className="max-w-4xl mx-auto text-center">
                <Reveal>
                    <Overline>Client Stories</Overline>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F0EBE0]">
                        Words that found <span className="italic text-[#C9A962]">their meaning</span>
                    </h2>
                </Reveal>

                <div className="mt-14 relative min-h-[260px] flex items-center justify-center">
                    <Quote size={48} className="absolute -top-4 left-1/2 -translate-x-1/2 text-[#C9A962]/15" />
                    <AnimatePresence mode="wait" custom={dir}>
                        <motion.figure
                            key={index}
                            data-testid="testimonial-card"
                            initial={{ opacity: 0, x: dir * 60 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: dir * -60 }}
                            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                            className="px-4"
                        >
                            <blockquote className="font-display italic text-xl sm:text-2xl lg:text-3xl font-light text-[#F0EBE0] leading-relaxed">
                                “{t.quote}”
                            </blockquote>
                            <figcaption className="mt-8">
                                <p data-testid="testimonial-name" className="text-sm font-semibold uppercase tracking-[0.2em] text-[#C9A962]">{t.name}</p>
                                <p className="mt-1 text-xs text-[#5C5A56] uppercase tracking-[0.15em]">{t.service}</p>
                            </figcaption>
                        </motion.figure>
                    </AnimatePresence>
                </div>

                <div className="mt-8 flex items-center justify-center gap-6">
                    <button
                        data-testid="testimonial-prev"
                        onClick={() => go(-1)}
                        className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-[#A19D94] hover:border-[#C9A962]/50 hover:text-[#C9A962] transition-colors"
                        aria-label="Previous testimonial"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <div className="flex gap-2">
                        {TESTIMONIALS.map((_, i) => (
                            <button
                                key={i}
                                data-testid={`testimonial-dot-${i}`}
                                onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
                                className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-[#C9A962]" : "w-1.5 bg-white/15 hover:bg-white/30"}`}
                                aria-label={`Go to testimonial ${i + 1}`}
                            />
                        ))}
                    </div>
                    <button
                        data-testid="testimonial-next"
                        onClick={() => go(1)}
                        className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-[#A19D94] hover:border-[#C9A962]/50 hover:text-[#C9A962] transition-colors"
                        aria-label="Next testimonial"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>
        </section>
    );
};
